import { BulkRatingResultSummary, IMPORT_STATS_JOB_STATUS } from 'cargo-chief-commons';
import {
	DEPTH_ABSOLUTE_FLOOR,
	DEPTH_ABSOLUTE_TARGET,
	LANE_CONFIDENCE_AMBER,
	LANE_CONFIDENCE_GREEN,
} from './lane-strength';

// CN-8872 (Bulk Rating): flat, per-row display values for the "interactive report" list.
// The shared tabbed-table renders custom cell types by reading well-known fields off the
// row, so we enrich each row with these three fields (via the rfpsList selector):
//  - rateConfidence: the 0–5 avg confidence, fed straight to a native <meter>.
//  - carrierDepth:   avg carriers/lane, fed to a color-matched <meter> (+ shown numerically).
//  - progress:       the file breakout (total/parsed/inserted/invalid) as a segmented bar
//                    + a hover tooltip, replacing the four separate count columns.
export const BULK_RATING_PROGRESS_COLUMN = 'progress';
export const BULK_RATING_RATE_CONFIDENCE_COLUMN = 'rateConfidence';
export const BULK_RATING_CARRIER_DEPTH_COLUMN = 'carrierDepth';
// CN-9039: the job-level Lane Confidence average. Blocked since CN-8854 §9 while the score
// existed only in the browser — a backend average would have had to re-implement the formula.
// mercury computes it now, so this falls out of the server-side move.
export const BULK_RATING_LANE_CONFIDENCE_COLUMN = 'laneConfidence';

// The interactive-report meter columns appended to the Bulk Rating list, as three
// index-aligned pieces the shared tabbed-table consumes together: header defs, per-column
// render types, and the displayed-column ids.
export interface BulkRatingMeterColumns {
	headers: { dataName: string; formattedName: string }[];
	dataTypes: string[];
	columns: string[];
}

// CN-8877: Avg Carrier Depth is a broker/shipper carrier-sourcing signal (avg carriers
// available per lane) and does not apply to carriers, so it is omitted when
// `includeCarrierDepth` is false. Progress + Avg Rate Confidence are always present. The three
// returned arrays stay index-aligned so dropping the column keeps the table rendering correct.
//
// CN-9039: Avg Lane Confidence rides the SAME gate as Avg Carrier Depth — the per-lane column it
// summarizes is broker + capacity only, so showing a carrier account a job-level average of a
// column it can't see would be worse than showing nothing.
export function bulkRatingMeterColumns(includeCarrierDepth: boolean): BulkRatingMeterColumns {
	return {
		headers: [
			{ dataName: BULK_RATING_PROGRESS_COLUMN, formattedName: 'Progress' },
			{ dataName: BULK_RATING_RATE_CONFIDENCE_COLUMN, formattedName: 'Avg Rate Confidence' },
			...(includeCarrierDepth
				? [
						{ dataName: BULK_RATING_CARRIER_DEPTH_COLUMN, formattedName: 'Avg Carrier Depth' },
						{ dataName: BULK_RATING_LANE_CONFIDENCE_COLUMN, formattedName: 'Avg Lane Confidence' },
					]
				: []),
		],
		dataTypes: [
			'progressMeter',
			'rateConfidenceMeter',
			...(includeCarrierDepth ? ['carrierDepthMeter', 'laneConfidenceMeter'] : []),
		],
		columns: [
			BULK_RATING_PROGRESS_COLUMN,
			BULK_RATING_RATE_CONFIDENCE_COLUMN,
			...(includeCarrierDepth
				? [BULK_RATING_CARRIER_DEPTH_COLUMN, BULK_RATING_LANE_CONFIDENCE_COLUMN]
				: []),
		],
	};
}

// Native <meter> attributes (matches the Capacity Search pricing panel confidence meter:
// min 0 / low 2 / high 4 / max 5 / optimum 5). Retune the rate-confidence bands here; the
// carrier-depth bands are sourced from lane-strength (see CARRIER_DEPTH_METER below).
export const RATE_CONFIDENCE_METER = { min: 0, low: 2, high: 4, max: 5, optimum: 5 };
// low/high come from lane-strength's DEPTH_ABSOLUTE_* so the count-column color and the Lane
// Confidence Capacity chip share one source and stay banded identically (retune in one place).
export const CARRIER_DEPTH_METER = {
	min: 0,
	low: DEPTH_ABSOLUTE_FLOOR,
	high: DEPTH_ABSOLUTE_TARGET,
	max: 15,
	optimum: 15,
};
// CN-8911: Lane Confidence meter (0–5). The native <meter> colors it red < amber / amber / green
// from LANE_CONFIDENCE_AMBER/GREEN. This colors the OVERALL score; the separate "Capacity" A/B/C
// chip is driven by depthBand on the depth axis. The two are kept from contradicting by the depth
// gate (Exposed depth can't yield a green meter), NOT by sharing these thresholds.
export const LANE_CONFIDENCE_METER = {
	min: 0,
	low: LANE_CONFIDENCE_AMBER,
	high: LANE_CONFIDENCE_GREEN,
	max: 5,
	optimum: 5,
};

export interface BulkRatingProgress {
	total: number;
	parsed: number;
	inserted: number;
	invalid: number;
	failed: number;
	// CN-8917: the job itself is in a FAILED terminal state (distinct from `failed`, the
	// count of failed rows). A failed job renders as a full red bar regardless of row counts.
	jobFailed: boolean;
	insertedPct: number;
	invalidPct: number;
	remainderPct: number;
	tooltip: string;
}

export interface BulkRatingRowDisplay {
	rateConfidence: number | null;
	carrierDepth: number | null;
	laneConfidence: number | null;
	// The numeric values live in the meters' hover tooltips (not inline). The confidence
	// tooltip also carries the pricing system's confidence-score explanation (mirrors the
	// Capacity Search pricing panel). Empty string = no tooltip (renders the "—" dash).
	rateConfidenceTooltip: string;
	carrierDepthTooltip: string;
	laneConfidenceTooltip: string;
	progress: BulkRatingProgress | null;
}

// Verbatim from the Capacity Search pricing panel confidence popover (pricing.component.html).
export const CONFIDENCE_EXPLANATION =
	'The confidence score measures how confident the pricing system is in its prediction. ' +
	'Confidence is based on which pricing model is used, how many recent transactions are ' +
	'available, and how much historical data you have for the lane.';

/**
 * Confidence-meter hover tooltip: the value out of 5 plus the pricing explanation.
 * Shared by the list (avg, per job) and the detail grid (per lane). '' = no value.
 */
export function buildRateConfidenceTooltip(
	value: number | null | undefined,
	label = 'Rate confidence',
): string {
	return value != null && Number.isFinite(value)
		? `${label} ${format1(value)} of 5. ${CONFIDENCE_EXPLANATION}`
		: '';
}

/**
 * Depth → color class matching the meter's red→yellow→green bands (CARRIER_DEPTH_METER).
 * Used to color-code the per-lane carrier COUNT on the detail grid (kept as a number, not a
 * meter). '' when there's no value.
 */
export function carrierDepthColorClass(value: number | null | undefined): string {
	if (value == null || !Number.isFinite(value)) {
		return '';
	}

	if (value >= CARRIER_DEPTH_METER.high) {
		return 'depth-high';
	}

	return value >= CARRIER_DEPTH_METER.low ? 'depth-mid' : 'depth-low';
}

interface BulkRatingRowLike {
	resultSummary?: BulkRatingResultSummary | null;
	totalRows?: number | null;
	rowsParsed?: number | null;
	rowsInserted?: number | null;
	invalidRows?: number | null;
	rowsFailed?: number | null;
	jobStatus?: string | null;
	failureReason?: string | null;
}

/** Enrich one list row with the meter/progress values the report columns render. */
export function deriveBulkRatingRowDisplay(
	row: BulkRatingRowLike | null | undefined,
): BulkRatingRowDisplay {
	const summary = row?.resultSummary;

	const rateConfidence =
		summary && summary.avgConfidence != null && Number.isFinite(summary.avgConfidence)
			? summary.avgConfidence
			: null;
	// Carrier depth is only meaningful when the account has capacity data on.
	const carrierDepth =
		summary &&
		summary.capacityEnabled &&
		summary.avgCarrierCount != null &&
		Number.isFinite(summary.avgCarrierCount)
			? summary.avgCarrierCount
			: null;

	// CN-9039: same capacity gate as carrierDepth. Absent on jobs completed before the score moved
	// server-side, which render the dash — the list has no per-lane data to fall back on, unlike
	// the results grid.
	const laneConfidence =
		summary &&
		summary.capacityEnabled &&
		summary.avgLaneConfidence != null &&
		Number.isFinite(summary.avgLaneConfidence)
			? summary.avgLaneConfidence
			: null;

	return {
		rateConfidence,
		carrierDepth,
		laneConfidence,
		rateConfidenceTooltip: buildRateConfidenceTooltip(rateConfidence, 'Avg rate confidence'),
		carrierDepthTooltip:
			carrierDepth != null ? `${format1(carrierDepth)} carriers / lane (avg)` : '',
		laneConfidenceTooltip: buildLaneConfidenceTooltip(laneConfidence, summary?.scoredLaneCount),
		progress: buildProgress(row),
	};
}

/**
 * Avg Lane Confidence hover tooltip. It ALWAYS names the scored-lane count, because the average
 * covers only the lanes that scored: without the count, a 4.6 over three lanes out of two hundred
 * reads exactly like a 4.6 over the whole book. '' = no value (the cell renders the dash).
 */
export function buildLaneConfidenceTooltip(
	value: number | null | undefined,
	scoredLaneCount: number | null | undefined
): string {
	if (value == null || !Number.isFinite(value)) {
		return '';
	}

	const base = `Avg lane confidence ${format1(value)} of 5`;

	if (scoredLaneCount == null || !Number.isFinite(scoredLaneCount)) {
		return `${base}, across the lanes that scored.`;
	}

	return `${base}, across ${scoredLaneCount} scored lane${scoredLaneCount === 1 ? '' : 's'}. ` +
		'Lanes without carrier depth or without a rate signal are not scored and are excluded.';
}

function format1(value: number): string {
	// One decimal, drop a trailing ".0" (5 → "5", 3.83 → "3.8").
	return Number(value.toFixed(1)).toString();
}

function buildProgress(row: BulkRatingRowLike | null | undefined): BulkRatingProgress | null {
	// CN-8917: a FAILED job always renders a full red bar — even with no rows — so a failed run
	// is never invisible on the list now that the Job Status column is gone. The tooltip carries
	// the failure reason (or a generic message). Queued / in-progress jobs are unaffected (they
	// fall through to the row-count logic below, and dash out when there are no rows yet).
	if (row?.jobStatus === IMPORT_STATS_JOB_STATUS.FAILED) {
		const reason = (row?.failureReason || '').trim();

		return {
			total: toCount(row?.totalRows),
			parsed: toCount(row?.rowsParsed),
			inserted: toCount(row?.rowsInserted),
			invalid: toCount(row?.invalidRows),
			failed: toCount(row?.rowsFailed),
			jobFailed: true,
			insertedPct: 0,
			invalidPct: 0,
			remainderPct: 0,
			tooltip: reason ? `Failed: ${reason}` : 'Failed — this job did not complete.',
		};
	}

	const total = toCount(row?.totalRows);

	if (!total) {
		return null;
	}

	const parsed = clamp(toCount(row?.rowsParsed), total);
	const inserted = clamp(toCount(row?.rowsInserted), total);
	const invalid = clamp(toCount(row?.invalidRows), total);
	const failed = clamp(toCount(row?.rowsFailed), total);

	// Bar segments over the total: priced (green) + problem rows (amber) + remainder (grey).
	// Cap the problem segment so priced + problem never exceed the total (widths stay <=100%).
	const problem = Math.min(invalid + failed, Math.max(0, total - inserted));
	const insertedPct = pct(inserted, total);
	const invalidPct = pct(problem, total);
	const remainderPct = Math.max(0, 100 - insertedPct - invalidPct);

	const tooltip =
		`Total ${total} · Parsed ${parsed} · Inserted ${inserted} · Invalid ${invalid}` +
		(failed ? ` · Failed ${failed}` : '');

	return {
		total,
		parsed,
		inserted,
		invalid,
		failed,
		jobFailed: false,
		insertedPct,
		invalidPct,
		remainderPct,
		tooltip,
	};
}

function toCount(value: number | null | undefined): number {
	return typeof value === 'number' && Number.isFinite(value) && value > 0 ? Math.round(value) : 0;
}

function clamp(value: number, max: number): number {
	return Math.min(Math.max(0, value), max);
}

function pct(value: number, total: number): number {
	return total > 0 ? Math.min(100, (value / total) * 100) : 0;
}
